/**
 * Library — book catalogue + issue/return + overdue list.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

function BooksTab({ onPickBook }) {
  const [books, setBooks] = useState([]);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState({ title: "", author: "", isbn: "", category: "", total_copies: 1, location: "" });

  const load = (query = "") => api.get("/library/books", { params: { q: query || undefined } }).then((r) => setBooks(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.title.trim()) { toast.error("Title required"); return; }
    await api.post("/library/books", { ...draft, total_copies: Number(draft.total_copies) || 1 });
    toast.success("Book added");
    setDraft({ title: "", author: "", isbn: "", category: "", total_copies: 1, location: "" });
    load(q);
  };

  return (
    <div className="space-y-4">
      <div className="vs-card p-4 space-y-3">
        <div className="eyebrow">Add book</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input data-testid="book-title" placeholder="Title *" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="bg-white" />
          <Input data-testid="book-author" placeholder="Author" value={draft.author} onChange={(e) => setDraft({ ...draft, author: e.target.value })} className="bg-white" />
          <Input data-testid="book-isbn" placeholder="ISBN" value={draft.isbn} onChange={(e) => setDraft({ ...draft, isbn: e.target.value })} className="bg-white font-mono" />
          <Input data-testid="book-category" placeholder="Category" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="bg-white" />
          <Input data-testid="book-copies" type="number" min="1" placeholder="Copies" value={draft.total_copies} onChange={(e) => setDraft({ ...draft, total_copies: e.target.value })} className="bg-white font-mono" />
          <Input data-testid="book-location" placeholder="Shelf / room" value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} className="bg-white" />
        </div>
        <Button data-testid="book-create" onClick={create}>Add</Button>
      </div>

      <div className="flex gap-2 items-end">
        <Input data-testid="book-search" placeholder="Search title/author/isbn…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white" />
        <Button variant="outline" onClick={() => load(q)} data-testid="book-search-btn">Search</Button>
      </div>

      <div className="vs-card divide-y">
        {books.map((b) => (
          <div key={b.id} className="p-3 flex items-center gap-3" data-testid={`book-row-${b.id}`}>
            <div className="flex-1">
              <div className="font-medium text-sm">{b.title}</div>
              <div className="text-xs text-[var(--vs-text-secondary)]">{b.author || "—"} · {b.category || "—"}</div>
              <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{b.isbn || "—"} · {b.location || "—"}</div>
            </div>
            <div className="text-sm font-mono">
              <span className={b.available_copies > 0 ? "text-emerald-600" : "text-red-600"}>{b.available_copies}</span>
              <span className="text-[var(--vs-text-secondary)]"> / {b.total_copies}</span>
            </div>
            <Button size="sm" data-testid={`issue-book-${b.id}`} disabled={b.available_copies <= 0} onClick={() => onPickBook(b)}>Issue</Button>
          </div>
        ))}
        {!books.length && <div className="p-4 text-center text-sm text-[var(--vs-text-secondary)]">No books found.</div>}
      </div>
    </div>
  );
}

function IssueTab({ bookToIssue, onIssued }) {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [loanDays, setLoanDays] = useState(14);

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data || []));
  }, []);

  const issue = async () => {
    if (!bookToIssue) { toast.error("Pick a book from the catalogue first"); return; }
    if (!studentId) { toast.error("Pick a student"); return; }
    try {
      await api.post("/library/issue", { book_id: bookToIssue.id, student_id: studentId, loan_days: Number(loanDays) || 14 });
      toast.success(`Issued ${bookToIssue.title}`);
      setStudentId("");
      onIssued();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="vs-card p-4 space-y-3" data-testid="issue-pane">
      <div className="text-sm">
        {bookToIssue ? (
          <span>Issuing <strong>{bookToIssue.title}</strong> ({bookToIssue.available_copies} copies available)</span>
        ) : (
          <span className="text-[var(--vs-text-secondary)]">Pick a book from the catalogue first using the “Issue” button.</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Select value={studentId} onValueChange={setStudentId}>
          <SelectTrigger data-testid="issue-student" className="bg-white"><SelectValue placeholder="Pick student" /></SelectTrigger>
          <SelectContent>{students.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} · {s.vs_id}</SelectItem>))}</SelectContent>
        </Select>
        <Input data-testid="issue-loan-days" type="number" min="1" max="60" value={loanDays} onChange={(e) => setLoanDays(e.target.value)} className="bg-white font-mono" />
        <Button data-testid="issue-submit" onClick={issue} disabled={!bookToIssue || !studentId}>Issue</Button>
      </div>
    </div>
  );
}

function OverdueTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/library/issuances/overdue").then((r) => setRows(r.data));
  }, []);
  const returnBook = async (id) => {
    try {
      const r = await api.post(`/library/issuances/${id}/return`, {});
      toast.success(`Returned · fine ₹${r.data.fine_amount}`);
      setRows((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };
  return (
    <div className="vs-card divide-y" data-testid="overdue-list">
      {rows.map((r) => (
        <div key={r.id} className="p-3 flex items-center gap-3" data-testid={`overdue-${r.id}`}>
          <div className="flex-1">
            <div className="font-medium text-sm">{r.book_title}</div>
            <div className="text-xs text-[var(--vs-text-secondary)]">{r.student_name} · due {r.due_date}</div>
          </div>
          <div className="text-sm font-mono text-red-600">{r.overdue_days}d · ₹{r.projected_fine}</div>
          <Button size="sm" variant="outline" data-testid={`return-${r.id}`} onClick={() => returnBook(r.id)}>Return</Button>
        </div>
      ))}
      {!rows.length && <div className="p-4 text-center text-sm text-[var(--vs-text-secondary)]">No overdue books.</div>}
    </div>
  );
}

function ActiveTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/library/issuances", { params: { only_active: true } }).then((r) => setRows(r.data)); }, []);
  const returnBook = async (id) => {
    try {
      const r = await api.post(`/library/issuances/${id}/return`, {});
      toast.success(`Returned · fine ₹${r.data.fine_amount}`);
      setRows((arr) => arr.filter((x) => x.id !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="vs-card divide-y" data-testid="active-list">
      {rows.map((r) => (
        <div key={r.id} className="p-3 flex items-center gap-3">
          <div className="flex-1">
            <div className="font-medium text-sm">{r.book_title}</div>
            <div className="text-xs text-[var(--vs-text-secondary)]">{r.student_name} · issued {r.issued_at} · due {r.due_date}</div>
          </div>
          <Button size="sm" variant="outline" data-testid={`active-return-${r.id}`} onClick={() => returnBook(r.id)}>Return</Button>
        </div>
      ))}
      {!rows.length && <div className="p-4 text-center text-sm text-[var(--vs-text-secondary)]">No active issuances.</div>}
    </div>
  );
}

export default function Library() {
  const [bookToIssue, setBookToIssue] = useState(null);
  const [tab, setTab] = useState("books");
  return (
    <DashboardLayout>
      <div className="vs-section">
        <h1 className="text-2xl font-semibold">Library</h1>
        <p className="text-sm text-[var(--vs-text-secondary)]">Book catalogue, issue/return ledger, and overdue tracking. Default fine is ₹5 per day past due.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="books" data-testid="tab-books">Books</TabsTrigger>
          <TabsTrigger value="issue" data-testid="tab-issue">Issue</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tab-overdue">Overdue</TabsTrigger>
        </TabsList>
        <TabsContent value="books">
          <BooksTab onPickBook={(b) => { setBookToIssue(b); setTab("issue"); }} />
        </TabsContent>
        <TabsContent value="issue"><IssueTab bookToIssue={bookToIssue} onIssued={() => setBookToIssue(null)} /></TabsContent>
        <TabsContent value="active"><ActiveTab /></TabsContent>
        <TabsContent value="overdue"><OverdueTab /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
